import React, { useEffect, useMemo } from 'react';
import { Portal, Modal } from 'react-native-paper';
import styled from '@emotion/native';
import { Linking, ScrollView, Image, ActivityIndicator } from 'react-native';
import { router } from 'expo-router';

import useStore from '@/stores/useStore';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { useChannelRouting } from '@/shared/hooks/useChannelRouting';
import useRouteThemeStore from '@/stores/useRouteThemeStore';

import Button from '@/shared/components/Button';
import { BodyText, H2 } from '@/shared/components/StyledText';
import { green, sand, mint, white } from '@/shared/styles/colors';
import { ROUTES } from '@/shared/constants/routes';
import { formatTotalDuration } from '@/shared/utils';
import { EMME_SUPPORT_EMAIL, IS_WEB } from '@/shared/constants/utility';
import type { RouteType } from '@/shared/types/navigation';
import type { IsDesktopProps } from '@/shared/styles/types';

type CallStatus = 'normal' | 'earlyEnd';
type Role = 'stylist' | 'client';

const TITLE_TEXT: Record<CallStatus, { desktop: string; mobile: string }> = {
  normal: {
    desktop: "That's a Wrap! Thanks for styling with EMME.",
    mobile: "That's a Wrap! Thanks for styling\nwith EMME.",
  },
  earlyEnd: {
    desktop: 'Thank you for styling with EMME.',
    mobile: 'Thank you for styling\nwith EMME.',
  },
};

const PostCallModal = () => {
  const { isDesktop } = useBreakpoint();
  const { navigateToChannel } = useChannelRouting();
  const { setOverride, clearOverride } = useRouteThemeStore();

  const {
    isPostCallModalVisible,
    setIsPostCallModalVisible,
    isStylist,
    completedSessionDuration,
    sessionChannelUrl,
    setSessionChannelUrl,
    setCompletedSessionDuration,
    estimatedSessionDuration,
    setEstimatedSessionDuration,
  } = useStore();

  const callStatus: CallStatus = useMemo(() => {
    if (completedSessionDuration == null || estimatedSessionDuration == null) {
      return 'normal';
    }

    // Convert session duration from seconds to minutes
    const sessionDurationMinutes = completedSessionDuration / 60;

    // Define early end thresholds based on estimated duration
    const earlyEndThresholds: Record<number, number> = {
      15: 10, // 15 min session ends early if < 10 min
      30: 20, // 30 min session ends early if < 20 min
      45: 30, // 45 min session ends early if < 30 min
      60: 40, // 60 min session ends early if < 40 min
    };

    const threshold = earlyEndThresholds[estimatedSessionDuration];
    if (threshold && sessionDurationMinutes < threshold) {
      return 'earlyEnd';
    }

    return 'normal';
  }, [completedSessionDuration, estimatedSessionDuration]);

  const MESSAGE_TEMPLATES = {
    stylist: {
      normal: (
        <StyledText>
          Don&apos;t forget to follow up with your client {'\n'} and check the
          Payment Portal {'\n'} for your earnings.
        </StyledText>
      ),
      earlyEnd: (
        <StyledText>
          Looks like we wrapped a little early! {'\n'} If anything else comes
          up, don&apos;t hesitate {'\n'} to message your client directly.
        </StyledText>
      ),
    },
    client: {
      normal: (
        <StyledText>
          If your stylist has any follow-up {'\n'} notes, we&apos;ll send them
          your way. {'\n'} Make sure{' '}
          <Link onPress={() => handleNavigateTo(ROUTES.SETTINGS)}>
            notifications
          </Link>{' '}
          are enabled.
        </StyledText>
      ),
      earlyEnd: (
        <StyledText>
          Looks like we wrapped a little early!{'\n'} If anything else comes up,
          don&apos;t hesitate {'\n'} to message your stylist directly.
        </StyledText>
      ),
    },
  };

  const role: Role = isStylist ? 'stylist' : 'client';
  const formattedDuration = formatTotalDuration(completedSessionDuration);
  const messageNode = MESSAGE_TEMPLATES[role][callStatus];

  // Override the safe area theming on iOS
  useEffect(() => {
    if (!IS_WEB && isPostCallModalVisible) {
      setOverride({
        topSafeAreaBackground: green,
        bottomSafeAreaBackground: green,
        statusBarStyle: 'light-content',
      });
    }

    if (!isPostCallModalVisible) {
      clearOverride();
    }
  }, [clearOverride, isPostCallModalVisible, setOverride]);

  const handleClose = () => {
    setCompletedSessionDuration(undefined);
    setSessionChannelUrl(undefined);
    setEstimatedSessionDuration(undefined);
    setIsPostCallModalVisible(false);
  };

  const handleNavigateTo = (route: RouteType) => {
    router.push(route.path);
    handleClose();
  };

  const handleNavigateToChat = () => {
    if (sessionChannelUrl) {
      navigateToChannel(sessionChannelUrl);
      handleClose();
    } else {
      handleNavigateTo(ROUTES.MESSAGES);
    }
  };

  // pick the correct title text
  const titleText = isDesktop
    ? TITLE_TEXT[callStatus].desktop
    : TITLE_TEXT[callStatus].mobile;

  return (
    <Portal>
      <Modal
        visible={isPostCallModalVisible}
        onDismiss={handleClose}
        style={{ alignItems: 'center' }}
        contentContainerStyle={isDesktop ? {} : { flex: 1, width: '100%' }}
        theme={{
          colors: {
            ...(isDesktop ? {} : { backdrop: 'transparent' }),
          },
        }}
      >
        <ScrollView
          style={{ width: '100%', height: '100%' }}
          contentContainerStyle={{
            flexGrow: 1,
            alignItems: 'center', // Needed to be able to exit via overlay on desktop
          }}
        >
          <Container isDesktop={isDesktop}>
            <CloseButton onPress={handleClose}>
              <CloseIcon source={require('@/assets/images/CloseIcon.png')} />
            </CloseButton>
            <MainContentContainer>
              <TitleContainer isDesktop={isDesktop}>
                <Title>{titleText}</Title>
              </TitleContainer>
              <CopyContainer
                marginBottom={isStylist || callStatus === 'earlyEnd' ? 30 : 44}
              >
                {completedSessionDuration == null ? (
                  <DurationLoadingContainer>
                    <ActivityIndicator size="small" color={sand} />
                  </DurationLoadingContainer>
                ) : (
                  <>
                    <StyledText bold marginBottom={22}>
                      Total Call Duration: {formattedDuration}.
                    </StyledText>
                    {messageNode}
                  </>
                )}
              </CopyContainer>
              <ButtonSection>
                {(isStylist || callStatus === 'earlyEnd') && (
                  <Button
                    text="Go to Chat"
                    variant="tertiaryLight"
                    size="medium"
                    customStyle={{ marginBottom: 20 }}
                    onPress={handleNavigateToChat}
                    leftIcon={
                      <Image
                        source={require('@/assets/images/ChatIcon.png')}
                        style={{ width: 20, height: 20, marginRight: 6 }}
                        tintColor={white}
                      />
                    }
                  />
                )}
                <Button
                  text="View Session Overview"
                  variant="secondary"
                  size="medium"
                  onPress={() => {
                    // @ts-expect-error - tab param is not typed
                    router.push(`${ROUTES.SESSIONS.path}?tab=completed`);
                    handleClose();
                  }}
                />
              </ButtonSection>
            </MainContentContainer>

            <HelpSection>
              <HelpText>
                We&apos;d love your thoughts on how the session went —{' '}
                <Link
                  style={{ lineHeight: 17.7 }}
                  onPress={() =>
                    Linking.openURL(`mailto:${EMME_SUPPORT_EMAIL}`)
                  }
                >
                  <HelpText bold>send your feedback</HelpText>
                </Link>{' '}
                to help us keep getting better.
              </HelpText>
            </HelpSection>
          </Container>
        </ScrollView>
      </Modal>
    </Portal>
  );
};

export default PostCallModal;

const Container = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  paddingTop: 68,
  paddingBottom: isDesktop ? 42 : 48,
  alignItems: 'center',
  width: '100%',
  backgroundColor: green,
  borderRadius: isDesktop ? 10 : 0,
  maxWidth: isDesktop ? 620 : '100%',
  height: isDesktop ? 'auto' : '100%',
  paddingHorizontal: isDesktop ? 110 : 30,
}));

const MainContentContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
});

const TitleContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  maxWidth: isDesktop ? 'auto' : 315,
  marginBottom: 24,
}));

const Title = styled(H2)({
  color: sand,
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: 0,
  lineHeight: 35.9,
});

const CopyContainer = styled.View<{ marginBottom: number }>(
  ({ marginBottom }) => ({
    width: '100%',
    maxWidth: 322,
    marginBottom,
  }),
);

const ButtonSection = styled.View({
  width: '100%',
  maxWidth: 330,
  marginHorizontal: 'auto',
});

const Link = styled(BodyText)({
  fontSize: 16,
  textDecorationStyle: 'solid',
  textDecorationLine: 'underline',
  textDecorationColor: sand,
  color: sand,
  fontWeight: 'bold',
});

const StyledText = styled(BodyText)<{ bold?: boolean; marginBottom?: number }>(
  ({ bold, marginBottom }) => ({
    color: sand,
    textAlign: 'center',
    fontSize: 16,
    lineHeight: 18,
    ...(bold && { fontWeight: 'bold' }),
    ...(marginBottom && { marginBottom }),
  }),
);

const HelpSection = styled.View({
  marginTop: 36,
  maxWidth: 242,
});

const HelpText = styled(BodyText)<{ bold?: boolean }>(({ bold }) => ({
  fontSize: 14,
  lineHeight: 17.7,
  letterSpacing: -0.15,
  textAlign: 'center',
  color: mint,
  ...(bold && { fontWeight: 'bold' }),
}));

const CloseButton = styled.Pressable<IsDesktopProps>(({ isDesktop }) => ({
  position: 'absolute',
  top: isDesktop ? 22 : 30,
  right: isDesktop ? 24 : 32,
  justifyContent: 'center',
  alignItems: 'center',
}));

const CloseIcon = styled.Image({
  width: 24,
  height: 24,
  resizeMode: 'contain',
  tintColor: sand,
});

const DurationLoadingContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 22,
});
